import React, {
    useState,
    useEffect,
    useCallback,
    useRef,
    useMemo,
} from 'react';

import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
    Card,
    Table,
    Button,
    Space,
    Typography,
    Select,
    Badge,
    Row,
    Col,
    message,
    Tooltip,
    Spin,
    Drawer,
    Form,
    Input,
    Dropdown,
} from 'antd';
import {
    UserOutlined,
    ReloadOutlined,
    EyeOutlined,
    CheckOutlined,
    CloseOutlined,
    FilterOutlined,
    IdcardOutlined,
    PhoneOutlined,
    MoreOutlined,
    DeleteOutlined,
    StopOutlined,
} from '@ant-design/icons';
import { formatDate } from '../../utils/format';
import AdminProfileDrawer from './AdminProfileDrawer';
import InfiniteLoaderWrapper from '../InfiniteLoaderWrapper';

import useGetAdmins from '../../hooks/admin-management/useGetAdmins';
import { RenderIf } from '../RenderIf';
import useGetPermissions from '@/hooks/admin-management/useGetPermissions';
import { getCurrentUser, api } from '@/services/authService';
import useGetRolesOfUser from '@/hooks/admin-management/useGetRolesOfUser';
import {
    PermissionSchema,
    RoleSchema,
} from '@/types/admin-management/admin-roles-types';
// PERMISSIONS are now fetched from backend API
import { ColumnsType } from 'antd/es/table';
import { useRootStore } from '@/store/root-store';
import { doesUserHaveAccess } from '@/utils/permissions';
import { PERMISSION_NAMES } from '@/constants/permissions';
import UserStatusBadge from './UserStatusBadge';

const { Text } = Typography;
const { Option } = Select;

const AdminUsers = ({ currentUser }) => {
    const navigate = useNavigate();

    // const currentUser = getCurrentUser()
    // const currentUserId = currentUser?.id;

    const { permissions, isUserSuperAdmin, roles } = useRootStore();
    
    // Debug: Log current user's permissions and roles
    console.log('Current user permissions:', permissions);
    console.log('Current user roles:', roles);
    console.log('Is super admin:', isUserSuperAdmin);

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    const status = searchParams.get('status') ?? '';
    const email = decodeURIComponent(searchParams.get('email') ?? '');
    const id = searchParams.get('id') ?? '';

    // Get the roles of the currently signed in user
    // const { data: rolesData, isPending } = useGetRolesOfUser(currentUserId);

    // // TODO: eventually store this in a global state that can be access from any where
    // const isUserSuperAdmin = useMemo(() => {
    //     if (rolesData && Array.isArray(rolesData.user_roles)) {
    //         return rolesData.user_roles.some(
    //             (role) =>
    //                 role.role_name.toLocaleLowerCase() === 'super administrator'
    //         );
    //     }
    //     return [];
    // }, [rolesData]);

    // Get the permissions for the role
    // const { data: permissionsData, isPending: permissionsDataIsPending } =
    //     useGetPermissions(rolesData?.user_roles, !isUserSuperAdmin);

    // Check if user has permission to view admin users
    const canViewAdmins = isUserSuperAdmin || 
        (Array.isArray(permissions) && permissions.length > 0 && 
         doesUserHaveAccess(permissions, PERMISSION_NAMES.GET_ADMIN_USERS));

    const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
        useGetAdmins(
            { status, email, id },
            canViewAdmins
        );

    const userData = data?.pages?.[0].admin_users
        ? data?.pages?.[0].admin_users
        : data?.pages ?? [];

    const [filters, setFilters] = useState<Record<string, string>>({});

    const [filterVisible, setFilterVisible] = useState(false);

    const [form] = Form.useForm();

    const [selectedUser, setSelectedUser] = useState(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(
        window.innerWidth < 1200
    );

    // Check if there are any active filters
    const hasActiveFilters = useMemo(() => {
        const activeFilterCount = Object.keys(filters).filter(
            (key) =>
                filters[key] !== undefined &&
                filters[key] !== null &&
                filters[key] !== '' &&
                !(Array.isArray(filters[key]) && filters[key].length === 0)
        ).length;
        return activeFilterCount > 0;
    }, [filters]);

    const handleFilterSubmit = useCallback((values) => {
        // Clean up the values by removing empty/undefined values
        const cleanValues: Record<string, string> = Object.entries(
            values
        ).reduce((acc, [key, value]) => {
            if (value !== undefined && value !== '' && value !== null) {
                acc[key] = value;
            }
            return acc;
        }, {});

        setFilters(cleanValues);
        const newSearchParams = new URLSearchParams();

        if (cleanValues?.email) {
            newSearchParams.set(
                'email',
                `${encodeURIComponent(cleanValues.email.valueOf())}`
            );
        }

        if (cleanValues?.id) {
            newSearchParams.set('id', `${cleanValues.id.valueOf()}`);
        }

        if (cleanValues?.status) {
            newSearchParams.set('status', `${cleanValues.status.valueOf()}`);
        }
        setFilterVisible(false);
        navigate(`?${newSearchParams.toString()}`, { replace: true });
    }, []);

    /**
     * Clear filters
     */
    const handleRefresh = () => {
        // Reset all filters
        setFilters({});
        handleResetFilters();

        navigate(window.location.pathname, { replace: true });
    };

    // Reset filters in the drawer
    const handleResetFilters = useCallback(() => {
        // Just reset form fields visually without any side effects
        form.resetFields();

        // Use a more direct approach to clear all fields
        const emptyValues = {
            id: undefined,
            name: undefined,
            status: undefined,
            type: undefined,
            email: undefined,
        };

        form.setFieldsValue(emptyValues);
    }, [form]);

    // Check window size on resize
    useEffect(() => {
        const checkWindowSize = () => {
            setIsSmallScreen(window.innerWidth < 1200);
        };

        window.addEventListener('resize', checkWindowSize);
        return () => window.removeEventListener('resize', checkWindowSize);
    }, []);

    const handleUserClick = async (user) => {
        setDrawerVisible(true);
        setSelectedUser((userData) => ({
            ...userData, // Preserve all original fields - REFACTOR: Why are we preserving original fields?
            id: user.id,
            firstname: user.firstname || '',
            lastname: user.lastname || '',
            name:
                `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim() ||
                'N/A',
            // Make sure email and phone_number are never empty strings
            email: user.email || user.email || 'N/A',
            phone_number: user.phone_number || user.phone_number || 'N/A',
            role: user.role || 'ADMIN',
            is_activated: user.is_activated === true,
            created: user.created || user.created_at,
            status: user.status || 'new',
        }));
    };

    // Handle status change for admin users
    const handleStatusChange = async (userId, newStatus) => {
        try {
            console.log('Attempting to update admin status:', { userId, newStatus });
            console.log('API URL:', `/admin/admins/${userId}`);
            const requestBody = {
                action: "modify-status",
                status: newStatus
            };
            console.log('Request payload:', requestBody);
            
            const response = await api.patch(`/admin/admins/${userId}`, requestBody);
            
            console.log('API response:', response);
            
            if (response.status === 200) {
                // Show appropriate success message based on action
                if (newStatus === 'approved') {
                    message.success('Admin restored successfully');
                } else if (newStatus === 'declined') {
                    message.success('Admin suspended successfully');
                } else {
                    message.success(`Admin status updated to ${newStatus}`);
                }
                
                // Refresh the data after status change
                await refetch();
            }
        } catch (error) {
            console.error('Error updating admin status:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response,
                status: error.response?.status,
                data: error.response?.data
            });
            message.error('Failed to update admin status');
        }
    };

    // Handle delete admin user
    const handleDeleteUser = async (userId) => {
        // Check if user is authenticated
        const token = sessionStorage.getItem("auth_token");
        if (!token) {
            message.error('Authentication required. Please log in again.');
            window.location.href = '/sign-in';
            return;
        }

        // Show confirmation dialog
        const confirmed = window.confirm(
            'Are you sure you want to delete this admin? This action cannot be undone.'
        );
        
        if (!confirmed) return;
        
        // Disable the delete button to prevent double-clicks
        const deleteButton = document.querySelector(`[data-user-id="${userId}"]`);
        if (deleteButton) {
            deleteButton.disabled = true;
            deleteButton.textContent = 'Deleting...';
        }
        
        try {
            message.loading('Deleting admin user...', 0); // Show loading message
            
            const response = await api.delete(`/admin/admins/${userId}`);
            
            if (response.status === 200 || response.status === 204) {
                message.destroy(); // Clear loading message
                message.success('Admin deleted successfully');
                
                // Refresh the data after successful deletion
                await refetch();
            }
        } catch (error) {
            message.destroy(); // Clear loading message
            console.error('Error deleting admin user:', error);
            
            // Provide more specific error messages
            if (error.response?.status === 401) {
                message.error('Authentication failed. Please log in again.');
                window.location.href = '/sign-in';
            } else if (error.response?.status === 403) {
                message.error('You do not have permission to delete this admin.');
            } else if (error.response?.status === 404) {
                message.error('Admin user not found. It may have already been deleted.');
                // Refresh the data to update the list
                await refetch();
            } else {
                message.error('Failed to delete admin user. Please try again.');
            }
        } finally {
            // Re-enable the delete button
            if (deleteButton) {
                deleteButton.disabled = false;
                deleteButton.textContent = 'Delete';
            }
        }
    };

    // Get action menu for admin users
    const getActionMenu = (record) => {
        const items = [
            {
                key: "view",
                label: "View Profile",
                icon: <EyeOutlined />,
                onClick: () => handleUserClick(record),
            },
        ];

        // Add status management actions based on current status
        // Only show these actions if user has permission to modify admin users
        if (isUserSuperAdmin || (Array.isArray(permissions) && permissions.length > 0 && doesUserHaveAccess(permissions, PERMISSION_NAMES.MODIFY_ADMIN_USER))) {
            if (record.status === 'approved') {
                items.push({
                    key: "suspend",
                    label: "Suspend Admin",
                    icon: <StopOutlined style={{ color: "#ff4d4f" }} />,
                    onClick: () => handleStatusChange(record.id, "declined"),
                });
            } else if (record.status === 'declined') {
                items.push({
                    key: "restore",
                    label: "Restore Admin",
                    icon: <CheckOutlined style={{ color: "#52c41a" }} />,
                    onClick: () => handleStatusChange(record.id, "approved"),
                });
            } else if (record.status === 'new' || record.status === 'pending-review') {
                items.push(
                    {
                        key: "approve",
                        label: "Approve Admin",
                        icon: <CheckOutlined style={{ color: "#52c41a" }} />,
                        onClick: () => handleStatusChange(record.id, "approved"),
                    },
                    {
                        key: "decline",
                        label: "Decline Admin",
                        icon: <StopOutlined style={{ color: "#ff4d4f" }} />,
                        onClick: () => handleStatusChange(record.id, "declined"),
                    }
                );
            }
        }

        // Add delete action (only if user has permission to modify admin users)
        if (isUserSuperAdmin || (Array.isArray(permissions) && permissions.length > 0 && doesUserHaveAccess(permissions, PERMISSION_NAMES.MODIFY_ADMIN_USER))) {
            items.push({
                key: "delete",
                label: "Delete Admin",
                icon: <DeleteOutlined style={{ color: "#ff4d4f" }} />,
                onClick: () => {
                    // Prevent double-clicks by disabling the menu item temporarily
                    const menuItem = document.querySelector(`[data-menu-key="delete-${record.id}"]`);
                    if (menuItem) {
                        menuItem.style.pointerEvents = 'none';
                        menuItem.style.opacity = '0.5';
                    }
                    
                    handleDeleteUser(record.id);
                },
                // Add a unique key to help with targeting
                'data-menu-key': `delete-${record.id}`,
            });
        }

        return items;
    };

    const columns: ColumnsType<any> = [
        {
            title: 'Name',
            key: 'name',
            render: (_, record) => (
                <Space>
                    <UserOutlined />
                    {`${record.firstname || ''} ${record.lastname || ''}`}
                </Space>
            ),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (email) => email || 'N/A',
        },
        {
            title: 'Phone',
            dataIndex: 'phone_number',
            key: 'phone',
            render: (phone) => phone || 'N/A',
        },

        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                return (
                    <UserStatusBadge
                        status={status}
                        color={status === 'declined' ? '#faad14' : undefined}
                        label={status === 'declined' ? 'SUSPENDED' : undefined}
                    />
                );
            },
        },

        {
            title: 'Created',
            dataIndex: 'created',
            key: 'created',
            render: (timestamp) => formatDate(timestamp) || 'N/A',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            render: (_, record) => (
                <Dropdown
                    menu={{ items: getActionMenu(record) }}
                    trigger={["click"]}
                    placement="bottomRight"
                >
                    <Button type="text" icon={<MoreOutlined />} />
                </Dropdown>
            ),
            hidden:
                !doesUserHaveAccess(
                    permissions,
                    PERMISSION_NAMES.GET_ADMIN_USER
                ) && !isUserSuperAdmin,
        },
    ];

    if (
        !(Array.isArray(permissions) && permissions.length > 0 && doesUserHaveAccess(permissions, PERMISSION_NAMES.GET_ADMIN_USERS)) &&
        !isUserSuperAdmin
    ) {
        // TODO: Show toast
        navigate(-1);
    }

    return (
        <Card
            title="Admin Users"
            // extra={renderFilters()}
            extra={
                <Space>
                    <Badge
                        dot={hasActiveFilters}
                        offset={[-5, 5]}
                        color="#1890ff"
                    >
                        <Button
                            icon={<FilterOutlined />}
                            onClick={() => setFilterVisible(true)}
                            type={hasActiveFilters ? 'primary' : 'default'}
                        >
                            Filter{' '}
                            {hasActiveFilters
                                ? `(${
                                      Object.keys(filters).filter(
                                          (key) =>
                                              filters[key] !== undefined &&
                                              filters[key] !== null &&
                                              filters[key] !== '' &&
                                              !(
                                                  Array.isArray(filters[key]) &&
                                                  filters[key].length === 0
                                              )
                                      ).length
                                  })`
                                : ''}
                        </Button>
                    </Badge>
                    <Button onClick={handleRefresh}>Refresh</Button>
                </Space>
            }
        >
            <div className="table-responsive">
                <InfiniteLoaderWrapper
                    hasNextPage={hasNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                >
                    <Table
                        columns={columns}
                        dataSource={userData}
                        rowKey="id"
                        loading={isLoading || isFetchingNextPage}
                        pagination={false}
                        scroll={isSmallScreen ? { x: 1100 } : undefined}
                    />
                </InfiniteLoaderWrapper>
            </div>

            <RenderIf
                condition={
                    drawerVisible &&
                    (doesUserHaveAccess(
                        permissions,
                        PERMISSION_NAMES.GET_ADMIN_USER
                    ) ||
                        isUserSuperAdmin)
                }
            >
                <AdminProfileDrawer
                    visible={drawerVisible}
                    onClose={() => {
                        setDrawerVisible(false);
                        setSelectedUser(null);
                    }}
                    user={selectedUser}
                    userType="admin"
                />
            </RenderIf>
            {/* Filter drawer */}
            <Drawer
                title="Filter Admin Users"
                placement="right"
                onClose={() => setFilterVisible(false)}
                open={filterVisible}
                width={400}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFilterSubmit}
                    initialValues={filters}
                >
                    <Form.Item
                        name="id"
                        label="User ID"
                    >
                        <Input
                            placeholder="Search by user ID"
                            prefix={
                                <IdcardOutlined style={{ color: '#1890ff' }} />
                            }
                            allowClear
                        />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label="Email"
                    >
                        <Input
                            placeholder="Search by email"
                            prefix={
                                <UserOutlined style={{ color: '#1890ff' }} />
                            }
                            allowClear
                        />
                    </Form.Item>

                    <Form.Item
                        name="status"
                        label="Status"
                    >
                        <Select
                            placeholder="Filter by Status"
                            style={{
                                width: '100%',
                                height: '50px',
                            }}
                            allowClear
                            // value={filters?.status}
                            // onChange={handleFilterChange}
                        >
                            <Option value="new">New</Option>
                            <Option value="approved">Approved</Option>
                            <Option value="declined">Declined</Option>
                            <Option value="deleted">Deleted</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item>
                        <Space
                            style={{
                                width: '100%',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Button onClick={handleResetFilters}>
                                Clear Selections
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<FilterOutlined />}
                            >
                                Apply Filters
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Drawer>
        </Card>
    );
};

AdminUsers.propTypes = {
    currentUser: PropTypes.shape({
        id: PropTypes.string,
        role: PropTypes.string,
        email: PropTypes.string,
    }),
};

AdminUsers.defaultProps = {
    currentUser: null,
};

export default AdminUsers;
